// src/oura/merchant/drawers/MonthDrawer.tsx
//
// One month of the term, or the later months folded into one row: what lands,
// the dates in it (the current month only), and its report. Opened from a row
// of Your plan on Work.
//
// This is where the Schedule view's "What happens next" and "This month's
// report" went: the next month's row opens its lands, and every month says
// when its report comes. A done month links its report when the server ties
// one to it (BE handoff "Report tied to its term month"); otherwise it points
// to Reports rather than claiming which document it was.

import type { ReactNode } from "react";
import type { ClientReportLink } from "@service/aeo/clientReview";
import { ArrowLink, Dot, Drawer, DrawerBlock, DrawerHead, LATE_TEXT, ListRow, TYPE, type Tone } from "@oura/ui";
import { cn } from "../../lib/cn";
import { OURA_ROOT } from "../../nav";
import { dayDate, shortDateOrNull } from "../../lib/oura";
import { landsOf, slipNote, type ComingDate, type MonthRow } from "../model";

/** The month's state, as the status line under its title says it. */
const STATE: Record<MonthRow["state"], { label: string; tone: Tone }> = {
    done: { label: "Done", tone: "green" },
    now: { label: "You are here", tone: "blue" },
    next: { label: "Coming", tone: "gray" },
    later: { label: "Coming", tone: "gray" },
};

export function MonthDrawer({
    row,
    dates,
    reports,
    plan,
    onOpenReport,
    onClose,
}: {
    /** The row on show; null keeps the drawer shut. */
    row: MonthRow | null;
    /** The dates coming up, listed under the current month. */
    dates: ComingDate[];
    reports: ClientReportLink[];
    /** The plan's name and reference, for the later months, where the term's shape is the subject. */
    plan: { name: string; ref: string } | null;
    onOpenReport: (reportId: string) => void;
    onClose: () => void;
}) {
    if (!row) return null;
    const status = STATE[row.state];

    if (row.months.length > 1) {
        const first = row.months[0];
        const last = row.months[row.months.length - 1];
        return (
            <Drawer open onClose={onClose}>
                <DrawerHead title={`Months ${first.termMonth} to ${last.termMonth}`} status={status} />
                <DrawerBlock label="What lands">
                    <Lines lines={landsOf(row.months)} tone="gray" />
                </DrawerBlock>
                <DrawerBlock label="Each month">
                    {row.months.map((m) => (
                        <ListRow key={m.termMonth} title={`Month ${m.termMonth} · ${m.window}`} meta={reportMeta(m.reportDate, "Report")} />
                    ))}
                </DrawerBlock>
                {plan && <p className={cn(TYPE.tm, "m-0 text-gray-400")}>{plan.name} · reference {plan.ref}</p>}
            </Drawer>
        );
    }

    const month = row.months[0];
    const sent = row.state === "done" ? reports.find((r) => r.termMonth === month.termMonth) : undefined;
    // Up to this month's report: the merchant schedule carries each month's
    // window as words only, so its report date is the edge the dates are cut at.
    const inMonth = row.state === "now" ? dates.filter((d) => d.date <= month.reportDate) : [];
    let report: ReactNode;
    if (sent) {
        report = (
            <span className="flex flex-wrap items-center gap-x-3">
                {reportMeta(sent.date, "Sent")}
                <ArrowLink onClick={() => onOpenReport(sent.id)}>Open it</ArrowLink>
            </span>
        );
    } else if (row.state === "done") {
        report = (
            <span className="flex flex-wrap items-center gap-x-3">
                {reportMeta(month.reportDate, "Report")}
                <ArrowLink to={`${OURA_ROOT}/reports`}>Your reports</ArrowLink>
            </span>
        );
    } else {
        report = reportMeta(month.reportDate, "Comes");
    }

    return (
        <Drawer open onClose={onClose}>
            <DrawerHead title={`Month ${month.termMonth} · ${month.window}`} status={status} />
            <DrawerBlock label="What lands">
                {month.lands.length > 0 ? <Lines lines={month.lands} tone={row.state === "done" ? "green" : "gray"} /> : <p className="m-0 text-gray-500">Nothing is scheduled this month.</p>}
            </DrawerBlock>
            {inMonth.length > 0 && (
                <DrawerBlock label="Dates">
                    {inMonth.map((d) => {
                        const slip = slipNote(d);
                        return (
                            <ListRow
                                key={d.key}
                                title={d.label}
                                sub={slip && <span className={slip.late ? LATE_TEXT : undefined}>{slip.text}</span>}
                                meta={dayDate(d.date)}
                            />
                        );
                    })}
                </DrawerBlock>
            )}
            {/* The current month's report is already one of its dates. */}
            {!inMonth.some((d) => d.kind === "report") && <DrawerBlock label="Report">{report}</DrawerBlock>}
        </Drawer>
    );
}

function reportMeta(date: string, word: string): string {
    const day = shortDateOrNull(date);
    return day ? `${word} ${day}` : `${word} date to be set`;
}

/** A short list of what lands, each line whole: a truncated line would hide the work. Green once done. */
function Lines({ lines, tone }: { lines: string[]; tone: "green" | "gray" }) {
    return (
        <ul className="list-plain m-0 mt-1 space-y-1.5 p-0">
            {lines.map((l) => (
                <li key={l} className="flex items-start gap-2.5">
                    <Dot tone={tone} className="mt-1.5" />
                    <span>{l}</span>
                </li>
            ))}
        </ul>
    );
}
